import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, mediaUrl } from '../services/api';
import { Button } from '../components/ui/button';
import { usePreinvoice } from '../context/PreinvoiceContext';
import { useToast } from '../context/ToastContext';
import { useWebAuth } from '../context/WebAuthContext';
import LoginModal from '../components/LoginModal';
import QuantityControl from '../components/QuantityControl';
import { trackProductEvent } from '../services/tracking';
import { isWeightKey } from '../utils/weight';
import CatalogBreadcrumbs from '../components/CatalogBreadcrumbs';
import RichContent from '../components/RichContent';
import SpecValue from '../components/SpecValue';

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = usePreinvoice();
  const { showToast } = useToast();
  const { isAuthed } = useWebAuth();

  const [loginOpen, setLoginOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [selectedBrandId, setSelectedBrandId] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .getProductById(id)
      // بک‌اند در بعضی نسخه‌ها محصول را مستقیم برمی‌گرداند و در بعضی نسخه‌ها داخل {data}
      .then((res) => mounted && setProduct(res?.data || res || null))
      .catch(() => mounted && setProduct(null))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [id]);

  const productBrands = useMemo(() => {
    return Array.isArray(product?.brands) ? product.brands.filter(Boolean) : [];
  }, [product]);

  useEffect(() => {
    if (productBrands.length === 1) {
      setSelectedBrandId(String(productBrands[0].id));
    } else {
      setSelectedBrandId('');
    }
  }, [productBrands]);

  const selectedBrand = useMemo(() => {
    return productBrands.find((b) => String(b.id) === String(selectedBrandId)) || null;
  }, [productBrands, selectedBrandId]);

  const specs = useMemo(() => {
    const raw = product?.tech_data;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'object') {
      return Object.entries(raw)
        .filter(([k]) => !isWeightKey(k))
        .map(([k, v]) => ({ key: k, value: v }));
    }
    return [];
  }, [product]);

  useEffect(() => {
    if (!product?.id) return;
    trackProductEvent('product_view', {
      product_item_id: product.id,
      category_id: product.category_id || null,
      category_slug: product.category_slug || null,
      path: `/product/${product.id}`,
      metadata: {
        title: product.title || product.name || '',
        sku: product.sku || product.external_code || product.barcode || '',
      },
    });
  }, [product?.id]);

  function handleAdd() {
    if (!product) return;
    if (!isAuthed) {
      setLoginOpen(true);
      return;
    }
    if (productBrands.length > 1 && !selectedBrand) {
      showToast({
        title: 'برند را انتخاب کنید',
        message: 'برای این محصول چند برند ثبت شده است؛ لطفاً برند موردنظر را انتخاب کنید.',
      });
      return;
    }

    addItem(
      {
        ...product,
        selected_brand_id: selectedBrand?.id || null,
        selected_brand_name: selectedBrand ? (selectedBrand.name || selectedBrand.title) : (product.brand_name || null),
        product_code: product.product_code || product.sku || product.external_code || product.barcode,
      },
      qty
    );

    trackProductEvent('add_to_preinvoice', {
      product_item_id: product.id,
      brand_id: selectedBrand?.id || null,
      category_id: product.category_id || null,
      category_slug: product.category_slug || null,
      source: 'product_page',
      metadata: {
        title: product.title || product.name || '',
        sku: product.sku || product.external_code || product.barcode || '',
        quantity: qty,
      },
    });

    showToast({
      title: 'به پیش‌فاکتور اضافه شد',
      message: 'برای مشاهده خلاصه پیش‌فاکتور کلیک کنید.',
      actionLabel: 'مشاهده پیش‌فاکتور',
      onAction: () => navigate('/preinvoice'),
    });
  }

  return (
    <main className="bg-gray-50 min-h-[70vh]" dir="rtl">
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} onSuccess={() => setLoginOpen(false)} />

      <div className="pp-container py-8">
        {product && <div className="mb-5"><CatalogBreadcrumbs items={product.breadcrumbs || []} product={product} /></div>}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-extrabold text-gray-900">{loading ? 'در حال بارگذاری...' : (product?.title || product?.name || product?.sku || 'محصول')}</h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              {product?.category_title ? (
                <>
                  <span className="ml-2">دسته:</span>
                  {product.category_slug ? (
                    <Link className="text-[#005bbb] hover:underline" to={`/subcategory/${product.category_slug}`}>{product.category_title}</Link>
                  ) : (
                    <span>{product.category_title}</span>
                  )}
                </>
              ) : (
                'اطلاعات محصول'
              )}
            </p>
          </div>

          <Button variant="outline" className="rounded-full" onClick={() => navigate(-1)}>
            بازگشت
          </Button>
        </div>

        {!loading && !product && (
          <div className="bg-white border border-gray-200 rounded-3xl p-6 text-sm text-gray-600">
            محصول پیدا نشد.
          </div>
        )}

        {product && (
          <div className="space-y-6">
            {/* HERO: تصویر راست + توضیحات چپ */}
            <div className="bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-sm">
              <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                {/* تصویر/کاور */}
                <div className="lg:col-span-5 lg:order-2">
              <div className="bg-white border border-gray-200 rounded-3xl overflow-hidden shadow-sm">
                <div className="aspect-[4/3] bg-gradient-to-br from-blue-50 to-white flex items-center justify-center">
                  {product.image_url ? (
                    <img src={mediaUrl(product.image_url)} alt={product.title || product.name || product.sku || 'product'} className="w-full h-full object-cover" />
                  ) : product.brand_logo ? (
                    <img src={mediaUrl(product.brand_logo)} alt={product.brand_name || product.title || product.name || product.sku || 'brand'} className="w-32 h-32 object-contain opacity-80" />
                  ) : (
                    <div className="text-sm text-gray-400">تصویر محصول ثبت نشده</div>
                  )}
                </div>
                <div className="p-5">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                    {product.sku && <span className="px-3 py-1 rounded-full bg-gray-100">SKU: {product.sku}</span>}
                    {product.brand_name && <span className="px-3 py-1 rounded-full bg-gray-100">برند: {product.brand_name}</span>}
                  </div>
                </div>
              </div>
                </div>

                {/* توضیحات */}
                <div className="lg:col-span-7 lg:order-1">
                  <div className="bg-white border border-gray-200 rounded-3xl shadow-sm p-6">
                    <h2 className="text-lg font-extrabold text-gray-900">توضیحات</h2>
                    <RichContent className="mt-3 text-sm text-gray-700 leading-8" html={product.description || product.short_desc || 'توضیحی برای این محصول ثبت نشده است.'} />
                  </div>
                </div>
              </div>
            </div>

            {/* جدول سطح ۴: عملیات آخر جدول */}
            <div className="bg-white border border-gray-200 rounded-3xl shadow-sm p-6">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-extrabold text-gray-900">مشخصات و گزینه‌ها</h2>
                <Link to="/preinvoice" className="text-sm font-bold text-primary hover:underline">
                  مشاهده پیش‌فاکتور
                </Link>
              </div>

              {specs.length === 0 ? (
                <div className="mt-4 text-sm text-gray-500">برای این محصول مشخصاتی ثبت نشده است.</div>
              ) : (
                <div className="mt-4 overflow-x-auto">
                  <div className="min-w-[720px] overflow-hidden rounded-2xl border border-gray-200">
                    <table className="w-full text-center text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-center font-bold text-gray-700 whitespace-nowrap">ردیف</th>
                          <th className="px-4 py-3 text-center font-bold text-gray-700 whitespace-nowrap">عنوان</th>
                          {specs.map((s, idx) => (
                            <th key={idx} className="px-4 py-3 text-center font-bold text-gray-700 whitespace-nowrap">
                              {s.key || s.title || '—'}
                            </th>
                          ))}
                          <th className="px-4 py-3 text-center font-bold text-gray-700 whitespace-nowrap">برند</th>
                          <th className="px-4 py-3 text-center font-bold text-gray-700 whitespace-nowrap">تعداد</th>
                          <th className="px-4 py-3 text-center font-bold text-gray-700 whitespace-nowrap">افزودن به پیش‌فاکتور</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t border-gray-200">
                          <td className="px-4 py-4 font-bold text-gray-500 whitespace-nowrap">۱</td>
                          <td className="px-4 py-4 font-bold text-gray-900 whitespace-nowrap">
                            {product.title || product.name || 'محصول'}
                          </td>
                          {specs.map((s, idx) => (
                            <td key={idx} className="px-4 py-4 text-gray-600 whitespace-nowrap">
                              <SpecValue name={s.key || s.title} value={s.value ?? s.val ?? '—'} />
                            </td>
                          ))}
                          <td className="px-4 py-4 min-w-[180px]">
                            {productBrands.length > 0 ? (
                              <select
                                value={selectedBrandId}
                                onChange={(e) => setSelectedBrandId(e.target.value)}
                                className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-center text-sm focus:outline-none focus:ring-2 focus:ring-[#005bbb]/30"
                              >
                                {productBrands.length > 1 && <option value="">انتخاب برند</option>}
                                {productBrands.map((b) => (
                                  <option key={b.id} value={b.id}>
                                    {b.name || b.title || 'برند'}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className="text-gray-400 text-xs">بدون برند</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <QuantityControl value={qty} onChange={setQty} className="h-9" inputClassName="h-9" buttonClassName="h-7 w-7" />
                          </td>
                          <td className="px-4 py-4">
                            <Button className="h-9 rounded-2xl px-4" onClick={handleAdd}>
                              افزودن
                            </Button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <RichContent html={product?.bottom_description} className="mt-10 text-sm text-gray-700" />
      </div>
    </main>
  );
}
